/**
 * TallyImportPage — 3-step wizard for one-month-catch-up from TallyPrime.
 *
 *   Step 1: Upload — pick the Day Book XML, server parses and returns summary
 *   Step 2: Map    — confirm/correct ledger mapping (auto-suggested)
 *   Step 3: Review — dry-run report, then Commit (atomic post + lock)
 *
 * Idempotent: re-uploading the same file is a no-op.  Vouchers carry the
 * Tally GUID so duplicate-prevention is at the row level.
 *
 * Past imports are listed below the wizard for audit reference.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert, App, Button, Card, Col, Descriptions, Divider, Empty, Input, Modal,
  Row, Select, Space, Spin, Statistic, Steps, Switch, Table, Tag, Tooltip,
  Typography, Upload,
} from 'antd'
import {
  CheckCircleOutlined, CloudUploadOutlined, DatabaseOutlined,
  DeploymentUnitOutlined, FileSearchOutlined, ImportOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography
const NUM = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' as const }

// ── Types (mirror backend schemas/tally_import.py) ───────────────────────
interface TallyEntry {
  ledger_name: string
  amount: string
  dr_cr: 'DR' | 'CR'
  is_party_ledger: boolean
  bill_ref?: string | null
  bill_ref_type?: string | null
}
interface TallyVoucher {
  tally_guid: string
  voucher_no: string
  voucher_type: string
  our_voucher_type: string | null
  voucher_date: string
  party_ledger?: string | null
  narration?: string | null
  invoice_no?: string | null
  invoice_date?: string | null
  entries: TallyEntry[]
  is_balanced: boolean
  blockers: string[]
}
interface LedgerMapping {
  tally_name: string
  our_ledger_code: string | null
  our_ledger_name: string | null
  confidence: 'exact' | 'fuzzy' | 'manual' | 'saved'
  is_party: boolean
  voucher_count: number
  total_debit: string
  total_credit: string
  // Vendor auto-suggestion (when is_party=true and code=VENDOR_AP)
  suggested_supplier_id: string | null
  suggested_supplier_name: string | null
  supplier_match_kind: 'exact' | 'fuzzy' | null
}
interface SupplierLite {
  id: string
  supplier_name: string
  gstin: string | null
}
// Per-row mapping state held on the page. Mirrors the backend
// LedgerMappingCorrection schema.
interface MappingEntry {
  code: string | null
  is_party: boolean
  save: boolean
  supplier_id?: string | null
  customer_id?: string | null
  new_supplier_name?: string | null
  new_supplier_gstin?: string | null
}
interface ParseSummary {
  file_name: string
  voucher_count: number
  voucher_type_breakdown: Record<string, number>
  period_start: string | null
  period_end: string | null
  total_debit: string
  total_credit: string
  is_balanced: boolean
  ledger_count: number
  matched_ledger_count: number
  unmatched_ledger_count: number
  ledgers: LedgerMapping[]
  vouchers: TallyVoucher[]
  blockers: string[]
}
interface DryRunIssue {
  voucher_no: string
  voucher_date: string
  severity: 'error' | 'warning'
  message: string
}
interface DryRunResponse {
  voucher_count: number
  will_post_count: number
  will_skip_count: number
  will_fail_count: number
  dr_total: string
  cr_total: string
  is_balanced: boolean
  period_start: string | null
  period_end: string | null
  period_locked_months: string[]
  issues: DryRunIssue[]
}
interface CommitResponse {
  import_id: string
  status: string
  voucher_count: number
  posted_count: number
  skipped_count: number
  error_count: number
  dr_total: string
  cr_total: string
  posted_at: string
  errors: DryRunIssue[]
}
interface ImportRow {
  id: string
  file_name: string
  period_start: string | null
  period_end: string | null
  voucher_count: number
  posted_count: number
  skipped_count: number
  error_count: number
  status: string
  dr_total: string | null
  cr_total: string | null
  created_at: string
  posted_at: string | null
}

// ── Standard system ledger codes the operator can pick from ──────────────
// Hand-curated to keep the dropdown short. Operator can type any code that
// exists in their plant's chart of accounts via the searchable dropdown.
const LEDGER_OPTIONS = [
  // Sundry party
  { value: 'VENDOR_AP',         label: 'Trade Payables (VENDOR_AP)' },
  { value: 'CUSTOMER_AR',       label: 'Trade Receivables (CUSTOMER_AR)' },
  // Bank / cash
  { value: 'BANK_CURRENT',      label: 'Bank — Current Account' },
  { value: 'CASH_ACCOUNT',      label: 'Cash in Hand' },
  // Inventory
  { value: 'RM_INVENTORY',      label: 'Raw Material Inventory' },
  { value: 'FG_INVENTORY',      label: 'Finished Goods Inventory' },
  { value: 'STORES_INVENTORY',  label: 'Stores & Spares' },
  // Direct cost
  { value: 'COTTON_CONSUMPTION',label: 'Cotton / RM Consumption' },
  { value: 'PACKING_MATERIAL',  label: 'Packing Material' },
  { value: 'POWER_FUEL',        label: 'Power & Fuel' },
  { value: 'WAGES_DIRECT',      label: 'Direct Wages' },
  { value: 'SALARY_PAYABLE',    label: 'Salary & Wages Payable' },
  { value: 'PF_ESI_PAYABLE',    label: 'PF / ESI Payable' },
  // GST
  { value: 'GST_INPUT_CGST',    label: 'GST Input CGST (receivable)' },
  { value: 'GST_INPUT_SGST',    label: 'GST Input SGST (receivable)' },
  { value: 'GST_INPUT_IGST',    label: 'GST Input IGST (receivable)' },
  { value: 'GST_INPUT_ITC',     label: 'GST Input ITC (legacy / unsplit)' },
  { value: 'GST_OUTPUT_CGST',   label: 'GST Output CGST' },
  { value: 'GST_OUTPUT_SGST',   label: 'GST Output SGST' },
  { value: 'GST_OUTPUT_IGST',   label: 'GST Output IGST' },
  { value: 'TDS_PAYABLE',       label: 'TDS Payable' },
  { value: 'TCS_PAYABLE',       label: 'TCS Payable' },
  // Income
  { value: 'YARN_SALES',        label: 'Yarn Sales — Domestic' },
  { value: 'YARN_SALES_EXPORT', label: 'Yarn Sales — Export' },
  { value: 'WASTE_SALES',       label: 'Waste / Scrap Sales' },
  { value: 'JOB_WORK_INCOME',   label: 'Job Work Income' },
  { value: 'OTHER_INCOME',      label: 'Other Operating Income' },
  { value: 'INTEREST_INCOME',   label: 'Interest / Financial Income' },
  { value: 'VENDOR_CLAIMS',     label: 'Vendor Claims / Discount Received' },
  // Misc liab
  { value: 'GRIR_CLEARING',     label: 'GR/IR Clearing' },
  { value: 'ACCRUED_EXPENSES',  label: 'Accrued Expenses' },
  { value: 'ADVANCE_TO_SUPPLIER', label: 'Advances to Suppliers' },
  // Equity / Long-term
  { value: 'SHARE_CAPITAL',     label: 'Share Capital' },
  { value: 'RETAINED_EARNINGS', label: 'Retained Earnings' },
  { value: 'TERM_LOAN',         label: 'Term Loan — Banks' },
  { value: 'UNSECURED_LOANS',   label: 'Unsecured Loans' },
  // Fixed
  { value: 'FA_PLANT_MACHINERY',label: 'Plant & Machinery' },
  { value: 'FA_BUILDINGS',      label: 'Buildings' },
  { value: 'FA_VEHICLES',       label: 'Vehicles' },
  { value: 'FA_FURNITURE',      label: 'Furniture & Fixtures' },
  { value: 'ACCUM_DEP',         label: 'Accumulated Depreciation' },
]

const fmt = (v: string | number | null | undefined, dp = 2) =>
  v == null ? '—' : Number(v).toLocaleString('en-IN',
    { minimumFractionDigits: dp, maximumFractionDigits: dp })

// ─────────────────────────────────────────────────────────────────────────
export default function TallyImportPage() {
  const { plantId, plants, setPlantId } = usePlantId()

  // Wizard state
  const [step, setStep] = useState(0)
  const [parsed, setParsed] = useState<ParseSummary | null>(null)
  const [mapping, setMapping] = useState<Record<string, MappingEntry>>({})
  const [dryRun, setDryRun] = useState<DryRunResponse | null>(null)

  // Suppliers — fetched once, used for the VENDOR_AP picker
  const { data: suppliers = [] } = useQuery<SupplierLite[]>({
    queryKey: ['suppliers-lite', plantId],
    queryFn: () => apiClient.get('/masters/suppliers',
      { params: { plant_id: plantId, page_size: 1000 } })
      .then(r => (r.data?.items ?? r.data ?? []).map((s: any) => ({
        id: s.id,
        supplier_name: s.supplier_name,
        gstin: s.gstin ?? null,
      }))),
    enabled: !!plantId,
  })

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <ImportOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Tally Import
      </Title>
      <Text type="secondary">
        One-month catch-up from TallyPrime.&nbsp;
        Export Day Book → XML, upload here, confirm ledger mapping, post atomically.
      </Text>

      <Card style={{ marginTop: 16 }}>
        <Row style={{ marginBottom: 16 }}>
          <Col xs={24} md="auto">
            <Select value={plantId} onChange={setPlantId}
              style={{ width: 280 }}
              options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))} />
          </Col>
        </Row>

        <Steps
          current={step}
          style={{ marginBottom: 24 }}
          items={[
            { title: 'Upload',  icon: <CloudUploadOutlined /> },
            { title: 'Map ledgers', icon: <DeploymentUnitOutlined /> },
            { title: 'Review & Post', icon: <CheckCircleOutlined /> },
          ]}
        />

        {step === 0 && (
          <UploadStep
            plantId={plantId}
            onParsed={(s) => {
              setParsed(s)
              // Initialise mapping from the auto-suggestions
              const m: typeof mapping = {}
              s.ledgers.forEach(l => {
                m[l.tally_name] = {
                  code: l.our_ledger_code,
                  is_party: l.is_party,
                  save: true,
                  supplier_id: l.suggested_supplier_id ?? null,
                }
              })
              setMapping(m)
              setStep(1)
            }}
          />
        )}

        {step === 1 && parsed && (
          <MappingStep
            parsed={parsed}
            mapping={mapping}
            suppliers={suppliers}
            onChange={setMapping}
            onBack={() => setStep(0)}
            onNext={async () => {
              try {
                const dr = await runDryRun(plantId, parsed, mapping)
                setDryRun(dr)
                setStep(2)
              } catch {
                /* error already toasted by runDryRun */
              }
            }}
          />
        )}

        {step === 2 && parsed && dryRun && (
          <ReviewStep
            plantId={plantId}
            parsed={parsed}
            mapping={mapping}
            dryRun={dryRun}
            onBack={() => setStep(1)}
            onCommitted={() => {
              setStep(0)
              setParsed(null)
              setDryRun(null)
              setMapping({})
            }}
          />
        )}
      </Card>

      {/* Past imports — audit reference */}
      <Card style={{ marginTop: 16 }}
        title={<Space><DatabaseOutlined />Past imports</Space>}>
        <PastImports plantId={plantId} />
      </Card>
    </div>
  )
}


// ─── Step 1: Upload ──────────────────────────────────────────────────────
function UploadStep({
  plantId, onParsed,
}: {
  plantId: string
  onParsed: (s: ParseSummary) => void
}) {
  const { message: msg } = App.useApp()
  const [busy, setBusy] = useState(false)

  const handleFile = async (file: File) => {
    setBusy(true)
    try {
      const fd = new FormData()
      fd.append('plant_id', plantId)
      fd.append('file', file)
      const res = await apiClient.post('/accounting/tally/parse', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      const data = res.data as ParseSummary
      msg.success(`Parsed ${data.voucher_count} voucher(s) from "${data.file_name}"`)
      onParsed(data)
    } catch (e: any) {
      msg.error(e.response?.data?.detail || 'Parse failed')
    } finally {
      setBusy(false)
    }
    return false   // prevent AntD upload default
  }

  return (
    <div style={{ textAlign: 'center', padding: '40px 16px' }}>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 24, textAlign: 'left' }}
        message="How to export from TallyPrime"
        description={
          <ol style={{ margin: '6px 0 0 18px', padding: 0, fontSize: 12, lineHeight: 1.7 }}>
            <li><b>Display More Reports</b> → <b>Day Book</b></li>
            <li>Set the period (e.g. F2 → 1 Apr to 30 Apr)</li>
            <li>Press <b>Alt+E</b> → choose <b>Export</b></li>
            <li>File Format: <b>XML</b> &nbsp;·&nbsp; Save the file</li>
            <li>Upload it here</li>
          </ol>
        }
      />

      <Upload.Dragger
        accept=".xml"
        beforeUpload={handleFile}
        showUploadList={false}
        maxCount={1}
        disabled={busy || !plantId}
        style={{ padding: 24 }}
      >
        <p style={{ fontSize: 48, color: '#1677ff', margin: 0 }}>
          {busy ? <Spin size="large" /> : <CloudUploadOutlined />}
        </p>
        <p style={{ fontSize: 16, fontWeight: 500, marginTop: 8 }}>
          {busy ? 'Parsing…' : 'Click or drag the Tally Day Book XML here'}
        </p>
        <p style={{ fontSize: 12, color: '#8c8c8c' }}>
          Single .xml file, up to 25 MB.&nbsp;
          Re-uploading the same file is safe — duplicate vouchers are skipped.
        </p>
      </Upload.Dragger>
    </div>
  )
}


// ─── Step 2: Mapping ─────────────────────────────────────────────────────
function MappingStep({
  parsed, mapping, suppliers, onChange, onBack, onNext,
}: {
  parsed: ParseSummary
  mapping: Record<string, MappingEntry>
  suppliers: SupplierLite[]
  onChange: (m: typeof mapping) => void
  onBack: () => void
  onNext: () => void
}) {
  const unmappedCount = Object.values(mapping).filter(m => !m.code).length
  // Party-ledger rows that need a supplier link but don't have one yet
  const partyMissingSupplierCount = Object.values(mapping).filter(
    m => m.code === 'VENDOR_AP' && !m.supplier_id && !m.new_supplier_name,
  ).length
  const allMapped = unmappedCount === 0 && partyMissingSupplierCount === 0

  // Build a memo'd list of rows so we don't churn on every keystroke
  const rows = useMemo(() => parsed.ledgers.map(l => ({
    ...l,
    code: mapping[l.tally_name]?.code ?? null,
    save: mapping[l.tally_name]?.save ?? true,
    supplier_id: mapping[l.tally_name]?.supplier_id ?? null,
    new_supplier_name: mapping[l.tally_name]?.new_supplier_name ?? null,
  })), [parsed.ledgers, mapping])

  const setRow = (tally_name: string, patch: Partial<MappingEntry>) => {
    onChange({ ...mapping, [tally_name]: { ...mapping[tally_name], ...patch } })
  }

  return (
    <>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Statistic title="Vouchers" value={parsed.voucher_count} />
        </Col>
        <Col xs={12} md={6}>
          <Statistic title="Period"
            value={parsed.period_start && parsed.period_end
              ? `${dayjs(parsed.period_start).format('DD MMM')} – ${dayjs(parsed.period_end).format('DD MMM YYYY')}`
              : '—'}
            valueStyle={{ fontSize: 16 }}
          />
        </Col>
        <Col xs={12} md={6}>
          <Statistic title="Unique ledgers" value={parsed.ledger_count} />
        </Col>
        <Col xs={12} md={6}>
          <Statistic title="Unmapped"
            valueStyle={{ color: unmappedCount > 0 ? '#cf1322' : '#52c41a' }}
            value={unmappedCount} />
        </Col>
      </Row>

      <Alert
        type={parsed.is_balanced ? 'success' : 'error'}
        showIcon
        style={{ marginBottom: 16 }}
        message={
          parsed.is_balanced
            ? <>File balanced &middot; <b>DR ₹{fmt(parsed.total_debit)}</b> = <b>CR ₹{fmt(parsed.total_credit)}</b></>
            : <>File <b>NOT balanced</b> &middot; DR ₹{fmt(parsed.total_debit)} ≠ CR ₹{fmt(parsed.total_credit)}</>
        }
        description={
          !parsed.is_balanced && (
            <Text style={{ fontSize: 12 }}>
              Tally file has internal imbalance. This usually means the
              export period was mid-voucher. Re-export with full days only.
            </Text>
          )
        }
      />

      <Text strong>Map each Tally ledger to one of your system ledgers.</Text>
      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
        Your choices are saved — next month's import will reuse them automatically.
      </Text>

      <Table size="small" rowKey="tally_name"
        dataSource={rows}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        scroll={{ x: 'max-content' }}
        rowClassName={(r) => !r.code ? 'tally-unmapped-row' : ''}
        columns={[
          { title: 'Tally Ledger', dataIndex: 'tally_name', width: 280,
            render: (v: string) => <Text strong style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</Text> },
          { title: 'Vouchers', dataIndex: 'voucher_count', align: 'right' as const, width: 90 },
          { title: 'DR ₹', dataIndex: 'total_debit', align: 'right' as const,
            render: (v: string) => <span style={NUM}>{fmt(v, 0)}</span> },
          { title: 'CR ₹', dataIndex: 'total_credit', align: 'right' as const,
            render: (v: string) => <span style={NUM}>{fmt(v, 0)}</span> },
          { title: 'Auto', dataIndex: 'confidence', width: 80,
            render: (v: string) => <ConfidenceTag conf={v} /> },
          { title: 'Map to →', dataIndex: 'code', width: 280,
            render: (_v, r) => (
              <Select
                value={r.code}
                placeholder="Select system ledger"
                showSearch
                allowClear
                style={{ width: '100%' }}
                options={LEDGER_OPTIONS}
                onChange={(code) => setRow(r.tally_name, {
                  code,
                  is_party: code === 'VENDOR_AP' || code === 'CUSTOMER_AR',
                  // Reset supplier link when re-mapping away from VENDOR_AP
                  supplier_id: code === 'VENDOR_AP' ? r.supplier_id : null,
                })}
                filterOption={(input, opt) =>
                  (opt?.label as string ?? '').toLowerCase().includes(input.toLowerCase()) ||
                  (opt?.value as string ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            ) },
          { title: 'Supplier link', dataIndex: 'supplier_id', width: 280,
            render: (_v, r) => r.code !== 'VENDOR_AP' ? (
              <Text type="secondary" style={{ fontSize: 11 }}>—</Text>
            ) : (
              <SupplierPicker
                row={r}
                suppliers={suppliers}
                onPick={(supplier_id) => setRow(r.tally_name, {
                  supplier_id, new_supplier_name: null, new_supplier_gstin: null,
                })}
                onCreateNew={(name, gstin) => setRow(r.tally_name, {
                  supplier_id: null,
                  new_supplier_name: name,
                  new_supplier_gstin: gstin,
                })}
              />
            ) },
          { title: 'Save for future', dataIndex: 'save', width: 120,
            render: (_v, r) => (
              <Switch size="small" checked={r.save}
                onChange={(v) => setRow(r.tally_name, { save: v })} />
            ) },
        ]}
      />

      <Row justify="space-between" style={{ marginTop: 24 }}>
        <Col><Button onClick={onBack}>Back</Button></Col>
        <Col>
          <Tooltip title={allMapped ? '' : (
            unmappedCount > 0
              ? `Map all ${unmappedCount} unmatched ledger(s) before continuing`
              : `${partyMissingSupplierCount} vendor ledger(s) missing supplier link — pick existing or create new`
          )}>
            <Button type="primary" disabled={!allMapped}
              icon={<FileSearchOutlined />} onClick={onNext}>
              Continue to dry-run
            </Button>
          </Tooltip>
        </Col>
      </Row>
    </>
  )
}

// ─── Supplier picker (used inside the mapping table) ───────────────────
// One row gets either:
//   - a Select with existing suppliers (auto-suggested if available)
//   - a "+ Create new" link that swaps into a name/GSTIN entry inline
// Backend persists the chosen supplier_id (or creates the new supplier on
// commit) in tally_ledger_mappings.supplier_id so next month auto-applies.
function SupplierPicker({
  row, suppliers, onPick, onCreateNew,
}: {
  row: { tally_name: string; supplier_id: string | null;
         new_supplier_name?: string | null;
         suggested_supplier_name?: string | null;
         supplier_match_kind?: 'exact' | 'fuzzy' | null }
  suppliers: SupplierLite[]
  onPick: (supplier_id: string | null) => void
  onCreateNew: (name: string, gstin: string | null) => void
}) {
  const [creating, setCreating] = useState(!!row.new_supplier_name)
  const [draftName, setDraftName] = useState(row.new_supplier_name ?? row.tally_name)
  const [draftGstin, setDraftGstin] = useState('')

  if (creating) {
    return (
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Input size="small" value={draftName}
          onChange={e => setDraftName(e.target.value)}
          placeholder="New supplier name"
          onBlur={() => onCreateNew(draftName, draftGstin || null)} />
        <Input size="small" value={draftGstin}
          onChange={e => setDraftGstin(e.target.value)}
          placeholder="GSTIN (optional)"
          onBlur={() => onCreateNew(draftName, draftGstin || null)} />
        <Button size="small" type="link" style={{ padding: 0, height: 'auto', fontSize: 11 }}
          onClick={() => { setCreating(false); onPick(null) }}>
          Cancel — pick existing
        </Button>
      </Space>
    )
  }

  return (
    <Space direction="vertical" size={2} style={{ width: '100%' }}>
      <Select
        size="small"
        value={row.supplier_id}
        placeholder={
          row.suggested_supplier_name
            ? `Suggested: ${row.suggested_supplier_name}`
            : 'Pick existing supplier'
        }
        showSearch
        allowClear
        style={{ width: '100%' }}
        options={suppliers.map(s => ({
          value: s.id,
          label: s.gstin
            ? `${s.supplier_name}  ·  ${s.gstin}`
            : s.supplier_name,
        }))}
        onChange={(v) => onPick(v ?? null)}
        filterOption={(input, opt) =>
          (opt?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
        }
      />
      {row.supplier_match_kind === 'fuzzy' && row.suggested_supplier_name && !row.supplier_id && (
        <Text type="warning" style={{ fontSize: 10 }}>
          ⚠ Fuzzy match — verify
        </Text>
      )}
      <Button size="small" type="link" style={{ padding: 0, height: 'auto', fontSize: 11 }}
        onClick={() => setCreating(true)}>
        + Create new supplier
      </Button>
    </Space>
  )
}


function ConfidenceTag({ conf }: { conf: string }) {
  if (conf === 'saved') return <Tag color="purple">SAVED</Tag>
  if (conf === 'exact') return <Tag color="green">AUTO</Tag>
  if (conf === 'fuzzy') return <Tag color="gold">FUZZY</Tag>
  return <Tag color="default">—</Tag>
}


// ─── Step 3: Review + Commit ─────────────────────────────────────────────
async function runDryRun(
  plantId: string,
  parsed: ParseSummary,
  mapping: Record<string, MappingEntry>,
): Promise<DryRunResponse> {
  const correctionList = Object.entries(mapping).map(([tally_name, m]) => ({
    tally_name,
    our_ledger_code: m.code,
    is_party: m.is_party,
    save_for_future: m.save,
    supplier_id: m.supplier_id ?? null,
    customer_id: m.customer_id ?? null,
    new_supplier_name: m.new_supplier_name ?? null,
    new_supplier_gstin: m.new_supplier_gstin ?? null,
  }))
  const res = await apiClient.post('/accounting/tally/dry-run', {
    plant_id: plantId,
    vouchers: parsed.vouchers,
    mapping: correctionList,
  })
  return res.data
}

function ReviewStep({
  plantId, parsed, mapping, dryRun, onBack, onCommitted,
}: {
  plantId: string
  parsed: ParseSummary
  mapping: Record<string, MappingEntry>
  dryRun: DryRunResponse
  onBack: () => void
  onCommitted: () => void
}) {
  const { message: msg, modal } = App.useApp()
  const qc = useQueryClient()

  const commitMut = useMutation({
    mutationFn: async (): Promise<CommitResponse> => {
      const correctionList = Object.entries(mapping).map(([tally_name, m]) => ({
        tally_name,
        our_ledger_code: m.code,
        is_party: m.is_party,
        save_for_future: m.save,
        supplier_id: m.supplier_id ?? null,
        customer_id: m.customer_id ?? null,
        new_supplier_name: m.new_supplier_name ?? null,
        new_supplier_gstin: m.new_supplier_gstin ?? null,
      }))
      const res = await apiClient.post('/accounting/tally/commit', {
        plant_id: plantId,
        file_name: parsed.file_name,
        vouchers: parsed.vouchers,
        mapping: correctionList,
        confirm: true,
      })
      return res.data
    },
    onSuccess: (data) => {
      msg.success(
        `Posted ${data.posted_count} voucher(s)` +
        (data.skipped_count > 0 ? ` (${data.skipped_count} skipped — already imported)` : '')
      )
      qc.invalidateQueries({ queryKey: ['tally-imports'] })
      onCommitted()
    },
    onError: (e: any) => msg.error(e.response?.data?.detail || 'Commit failed'),
  })

  const blocked = dryRun.will_fail_count > 0 || dryRun.period_locked_months.length > 0

  const confirmCommit = () => {
    modal.confirm({
      title: `Post ${dryRun.will_post_count} voucher(s) to the GL?`,
      content: (
        <div>
          <p>
            This will atomically post <b>{dryRun.will_post_count}</b> voucher(s) into the
            general ledger and persist your ledger mapping.
          </p>
          <p style={{ marginBottom: 0 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Idempotent — already-imported vouchers (by Tally GUID) will be skipped.
              All-or-nothing: any failure rolls back the entire batch.
            </Text>
          </p>
        </div>
      ),
      okText: `Post ${dryRun.will_post_count} voucher(s)`,
      okButtonProps: { type: 'primary' },
      cancelText: 'Cancel',
      onOk: () => commitMut.mutate(),
    })
  }

  const errors = dryRun.issues.filter(i => i.severity === 'error')
  const warnings = dryRun.issues.filter(i => i.severity === 'warning')

  return (
    <>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small" styles={{ body: { padding: 12 } }}>
            <Text type="secondary" style={{ fontSize: 11 }}>Will post</Text>
            <div style={{ ...NUM, fontSize: 22, fontWeight: 700, color: '#52c41a' }}>
              {dryRun.will_post_count}
            </div>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" styles={{ body: { padding: 12 } }}>
            <Text type="secondary" style={{ fontSize: 11 }}>Will skip (already in)</Text>
            <div style={{ ...NUM, fontSize: 22, fontWeight: 700, color: '#1677ff' }}>
              {dryRun.will_skip_count}
            </div>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" styles={{ body: { padding: 12 } }}>
            <Text type="secondary" style={{ fontSize: 11 }}>Will fail</Text>
            <div style={{ ...NUM, fontSize: 22, fontWeight: 700,
              color: dryRun.will_fail_count > 0 ? '#cf1322' : '#262626' }}>
              {dryRun.will_fail_count}
            </div>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small" styles={{ body: { padding: 12 } }}>
            <Text type="secondary" style={{ fontSize: 11 }}>DR / CR balance</Text>
            <div style={{ ...NUM, fontSize: 14, fontWeight: 600 }}>
              ₹{fmt(dryRun.dr_total, 0)}<br/>
              ₹{fmt(dryRun.cr_total, 0)}
            </div>
            <Tag color={dryRun.is_balanced ? 'success' : 'error'} style={{ marginTop: 4 }}>
              {dryRun.is_balanced ? 'BALANCED' : 'IMBALANCED'}
            </Tag>
          </Card>
        </Col>
      </Row>

      {dryRun.period_locked_months.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          icon={<WarningOutlined />}
          message="Cannot commit — period(s) closed"
          description={
            <>
              The following month(s) are CLOSED in your monthly close: &nbsp;
              <b>{dryRun.period_locked_months.join(', ')}</b>.&nbsp;
              Either unlock the period or change the voucher dates in Tally
              and re-export.
            </>
          }
        />
      )}

      {errors.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={`${errors.length} error(s) — these vouchers will not post`}
          description={
            <Table size="small" pagination={false}
              dataSource={errors.slice(0, 50)} rowKey={(_, i) => String(i)}
              columns={[
                { title: 'Voucher', dataIndex: 'voucher_no', width: 120,
                  render: (v: string) => <Text style={{ fontFamily: 'monospace' }}>{v}</Text> },
                { title: 'Date', dataIndex: 'voucher_date', width: 110,
                  render: (v: string) => dayjs(v).format('DD-MMM-YY') },
                { title: 'Error', dataIndex: 'message',
                  render: (v: string) => <Text style={{ fontSize: 12 }}>{v}</Text> },
              ]}
            />
          }
        />
      )}

      {warnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`${warnings.length} warning(s) — informational, won't block posting`}
          description={
            <Table size="small" pagination={false}
              dataSource={warnings.slice(0, 50)} rowKey={(_, i) => String(i)}
              columns={[
                { title: 'Voucher', dataIndex: 'voucher_no', width: 120,
                  render: (v: string) => <Text style={{ fontFamily: 'monospace' }}>{v}</Text> },
                { title: 'Note', dataIndex: 'message',
                  render: (v: string) => <Text style={{ fontSize: 12 }}>{v}</Text> },
              ]}
            />
          }
        />
      )}

      <Divider>Voucher type breakdown</Divider>
      <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }} bordered>
        {Object.entries(parsed.voucher_type_breakdown).map(([k, v]) => (
          <Descriptions.Item key={k} label={k}>{v}</Descriptions.Item>
        ))}
      </Descriptions>

      <Row justify="space-between" style={{ marginTop: 24 }}>
        <Col><Button onClick={onBack}>Back to mapping</Button></Col>
        <Col>
          <Space>
            <Tooltip title={blocked
              ? 'Fix the blocking errors above before posting'
              : 'Atomic posting — all or nothing'}>
              <Button type="primary"
                size="large"
                disabled={blocked || dryRun.will_post_count === 0}
                loading={commitMut.isPending}
                icon={<CheckCircleOutlined />}
                onClick={confirmCommit}>
                Post {dryRun.will_post_count} voucher(s) to GL
              </Button>
            </Tooltip>
          </Space>
        </Col>
      </Row>
    </>
  )
}


// ─── Past imports table ──────────────────────────────────────────────────
function PastImports({ plantId }: { plantId: string }) {
  const { data = [], isLoading } = useQuery<ImportRow[]>({
    queryKey: ['tally-imports', plantId],
    queryFn: () => apiClient.get('/accounting/tally/imports',
      { params: { plant_id: plantId } }).then(r => r.data),
    enabled: !!plantId,
  })

  if (isLoading) return <Spin />
  if (data.length === 0) return <Empty description="No previous imports" />

  return (
    <Table size="small" dataSource={data} rowKey="id"
      pagination={{ pageSize: 10 }}
      scroll={{ x: 'max-content' }}
      columns={[
        { title: 'File', dataIndex: 'file_name', ellipsis: true,
          render: (v: string) => <Text style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</Text> },
        { title: 'Period', key: 'period',
          render: (_, r) => r.period_start
            ? `${dayjs(r.period_start).format('DD-MMM')} – ${dayjs(r.period_end!).format('DD-MMM-YY')}`
            : '—' },
        { title: 'Vouchers', dataIndex: 'voucher_count', align: 'right' as const },
        { title: 'Posted', dataIndex: 'posted_count', align: 'right' as const,
          render: (v: number) => <Text style={{ color: '#52c41a' }}>{v}</Text> },
        { title: 'Skipped', dataIndex: 'skipped_count', align: 'right' as const,
          render: (v: number) => <Text type="secondary">{v}</Text> },
        { title: 'Errors', dataIndex: 'error_count', align: 'right' as const,
          render: (v: number) => v > 0 ? <Text style={{ color: '#cf1322' }}>{v}</Text> : '—' },
        { title: 'DR ₹', dataIndex: 'dr_total', align: 'right' as const,
          render: (v: string | null) => v ? `₹${fmt(v, 0)}` : '—' },
        { title: 'Status', dataIndex: 'status',
          render: (v: string) => (
            <Tag color={v === 'POSTED' ? 'success' : v === 'FAILED' ? 'error' : 'default'}>
              {v}
            </Tag>
          ) },
        { title: 'Posted at', dataIndex: 'posted_at',
          render: (v: string | null) => v ? dayjs(v).format('DD-MMM-YY HH:mm') : '—' },
      ]}
    />
  )
}
